import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { VisitorTracker } from './components/VisitorTracker';

// Landing loads eagerly (main entry), everything else is code-split
import { LandingPage } from './pages/LandingPage';
import { captureUtm } from './lib/utm';

const VykupPage = lazy(() => import('./pages/services/VykupPage'));
const OplataPage = lazy(() => import('./pages/services/OplataPage'));
const PostachalnykyPage = lazy(() => import('./pages/services/PostachalnykyPage'));
const ZbirniVantazhiPage = lazy(() => import('./pages/services/ZbirniVantazhiPage'));
const StrakhuvanniaPage = lazy(() => import('./pages/services/StrakhuvanniaPage'));
const PravylaPage = lazy(() => import('./pages/services/PravylaPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const LoginPage = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.RegisterPage })));
const DashboardLayout = lazy(() => import('./pages/DashboardLayout').then(m => ({ default: m.DashboardLayout })));
const DashboardOverview = lazy(() => import('./pages/DashboardOverview').then(m => ({ default: m.DashboardOverview })));
const ParcelsList = lazy(() => import('./pages/ParcelsPage').then(m => ({ default: m.ParcelsList })));
const AddParcel = lazy(() => import('./pages/ParcelsPage').then(m => ({ default: m.AddParcel })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage').then(m => ({ default: m.CalculatorPage })));
const BalanceHistoryPage = lazy(() => import('./pages/BalanceHistoryPage').then(m => ({ default: m.BalanceHistoryPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const USADeliveryPage = lazy(() => import('./pages/USADeliveryPage').then(m => ({ default: m.USADeliveryPage })));
const USARequestsPage = lazy(() => import('./pages/USARequestsPage').then(m => ({ default: m.USARequestsPage })));
const SupplierSearchPage = lazy(() => import('./pages/SupplierSearchPage').then(m => ({ default: m.SupplierSearchPage })));
const MyVykupPage = lazy(() => import('./pages/MyVykupPage').then(m => ({ default: m.MyVykupPage })));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage').then(m => ({ default: m.SuppliersPage })));
const ForumPage = lazy(() => import('./pages/ForumPage').then(m => ({ default: m.ForumPage })));
const ForumTopicPage = lazy(() => import('./pages/ForumPage').then(m => ({ default: m.ForumTopicPage })));
const AdminLayout = lazy(() => import('./pages/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminOverview = lazy(() => import('./pages/AdminOverview').then(m => ({ default: m.AdminOverview })));
const AdminParcels = lazy(() => import('./pages/AdminParcels').then(m => ({ default: m.AdminParcels })));
const AdminInvoices = lazy(() => import('./pages/AdminInvoices').then(m => ({ default: m.AdminInvoices })));
const AdminUsers = lazy(() => import('./pages/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminCurrencyRequests = lazy(() => import('./pages/AdminCurrencyRequests').then(m => ({ default: m.AdminCurrencyRequests })));
const AdminSettings = lazy(() => import('./pages/AdminSettings').then(m => ({ default: m.AdminSettings })));
const AdminDeletedParcels = lazy(() => import('./pages/AdminDeletedParcels').then(m => ({ default: m.AdminDeletedParcels })));
const AdminEmailService = lazy(() => import('./pages/AdminEmailService').then(m => ({ default: m.AdminEmailService })));
const AdminProfit = lazy(() => import('./pages/AdminProfit').then(m => ({ default: m.AdminProfit })));
const AdminVisitors = lazy(() => import('./pages/AdminVisitors').then(m => ({ default: m.AdminVisitors })));
const AdminShipments = lazy(() => import('./pages/AdminShipments').then(m => ({ default: m.AdminShipments })));
const AdminUSARequests = lazy(() => import('./pages/AdminUSARequests').then(m => ({ default: m.AdminUSARequests })));
const AdminUSAPayments = lazy(() => import('./pages/AdminUSAPayments').then(m => ({ default: m.AdminUSAPayments })));
const AdminLoyalty = lazy(() => import('./pages/AdminLoyalty').then(m => ({ default: m.AdminLoyalty })));
const AdminShop = lazy(() => import('./pages/AdminShop').then(m => ({ default: m.AdminShop })));
const AdminSEO = lazy(() => import('./pages/AdminSEO').then(m => ({ default: m.AdminSEO })));
const AdminShipping = lazy(() => import('./pages/AdminShipping').then(m => ({ default: m.AdminShipping })));
const AdminBlog = lazy(() => import('./pages/AdminBlog').then(m => ({ default: m.AdminBlog })));
const AdminNews = lazy(() => import('./pages/AdminNews').then(m => ({ default: m.AdminNews })));
const AdminLeads = lazy(() => import('./pages/AdminLeads').then(m => ({ default: m.AdminLeads })));
const AdminVykup = lazy(() => import('./pages/AdminVykup').then(m => ({ default: m.AdminVykup })));
const LandingAdsPage = lazy(() => import('./pages/LandingAdsPage'));

import './App.css';

captureUtm();

const PageLoader = () => (
  <div className="min-h-screen bg-[#060a13] flex items-center justify-center">
    <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <VisitorTracker />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/zayavka" element={<LandingAdsPage />} />
          <Route path="/zayavka/:offer" element={<LandingAdsPage />} />
          <Route path="/usa" element={<USADeliveryPage />} />
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/forum/:topicId" element={<ForumTopicPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/vykup-tovariv-z-kytayu" element={<VykupPage />} />
          <Route path="/oplata-postachalnykam-u-kytai" element={<OplataPage />} />
          <Route path="/poshuk-postachalnykiv-v-kytai" element={<PostachalnykyPage />} />
          <Route path="/dostavka-zbirnyh-vantazhiv" element={<ZbirniVantazhiPage />} />
          <Route path="/strakhuvannya-vantazhu" element={<StrakhuvanniaPage />} />
          <Route path="/pravyla-perevezen" element={<PravylaPage />} />
          <Route path="/vidguky" element={<ReviewsPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          
          {/* Client dashboard routes */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardOverview />} />
            <Route path="parcels" element={<ParcelsList />} />
            <Route path="parcels/new" element={<AddParcel />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="calculator" element={<CalculatorPage />} />
            <Route path="balance" element={<BalanceHistoryPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="usa-requests" element={<USARequestsPage />} />
            <Route path="orders" element={<ShopPage />} />
            <Route path="supplier-search" element={<SupplierSearchPage />} />
            <Route path="my-vykup" element={<MyVykupPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
          </Route>
          
          {/* Admin routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="parcels" element={<AdminParcels />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="profit" element={<AdminProfit />} />
            <Route path="visitors" element={<AdminVisitors />} />
            <Route path="shipments" element={<AdminShipments />} />
            <Route path="currency-requests" element={<AdminCurrencyRequests />} />
            <Route path="deleted-parcels" element={<AdminDeletedParcels />} />
            <Route path="email" element={<AdminEmailService />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="usa-requests" element={<AdminUSARequests />} />
            <Route path="usa-payments" element={<AdminUSAPayments />} />
            <Route path="loyalty" element={<AdminLoyalty />} />
            <Route path="shop" element={<AdminShop />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="vykup" element={<AdminVykup />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
